import * as THREE from 'three';

/**
 * Updates the orbit position of an object based on elapsed time.
 * @param {THREE.Object3D} group - The object group to update.
 * @param {number} elapsedTime - Elapsed time from the clock.
 * @param {number} orbitRadius - Radius of the orbit.
 * @param {number} orbitSpeed - Speed of the orbit.
 * @param {THREE.Vector3} [offset] - Optional offset (e.g., parent position).
 */
function updateOrbit(group, elapsedTime, orbitRadius, orbitSpeed, offset = new THREE.Vector3()) {
  if (!group || !Number.isFinite(orbitRadius) || !Number.isFinite(orbitSpeed)) return;
  const angle = elapsedTime * orbitSpeed;
  group.position.x = Math.cos(angle) * orbitRadius + offset.x;
  group.position.z = Math.sin(angle) * orbitRadius + offset.z;
}

/**
 * Animates space objects (planets, moons, comet, etc.) based on elapsed time.
 * @param {Object} objects - Collections of space objects (planets, moons, comet, etc.).
 * @param {THREE.Clock} clock - Three.js clock for consistent timing.
 * @param {THREE.Camera} camera - Three.js camera for distance-based culling.
 * @param {THREE.Scene} scene - Three.js scene for potential dynamic modifications.
 * @param {Function} updateInfoCards - Function to update UI info cards.
 */
export function animateSpaceObjects(objects, clock, camera, scene, updateInfoCards) {
  if (!objects || !clock || !camera || !scene) {
    console.error('Missing required parameters in animateSpaceObjects');
    return;
  }

  const elapsedTime = clock.getElapsedTime();
  const tempVector = new THREE.Vector3();
  const tangent = new THREE.Vector3();
  const lookPoint = new THREE.Vector3();

  // Animate planets
  objects.planets?.forEach((planet, index) => {
    if (planet?.group && planet?.mesh && planet?.body && planet.orbit?.radius && planet.orbit?.speed) {
      const planetTime = elapsedTime * planet.orbit.speed * 0.5; // Adjusted for balance
      updateOrbit(planet.group, planetTime, planet.orbit.radius, 1);
      planet.mesh.rotation.y += 0.04;
      planet.body.position.copy(planet.group.position);

      // Lighting effect for LOD child
      if (planet.mesh.children[0]?.material?.emissive) {
        tempVector.copy(planet.group.position).normalize();
        const dotProduct = tempVector.dot(new THREE.Vector3(1, 0, 0));
        const normalizedDot = (dotProduct + 1) / 2;
        planet.mesh.children[0].material.emissiveIntensity = 0.1 + normalizedDot * 0.2;
      }
    }
  });

  // Animate moons with distance culling
  objects.moons?.forEach(moon => {
    if (moon?.mesh && moon?.body && moon?.group?.parent && moon.orbitSpeed && moon.orbitRadius) {
      moon.mesh.getWorldPosition(tempVector);
      if (camera.position.distanceTo(tempVector) < 500) {
        const moonTime = elapsedTime * moon.orbitSpeed * 0.2;
        const parentPos = moon.group.parent.position;
        updateOrbit(moon.mesh, moonTime, moon.orbitRadius, 1, parentPos);
        moon.mesh.rotation.y += 0.02;
        moon.body.position.copy(tempVector.addVectors(parentPos, moon.mesh.position));

        if (moon.mesh.material?.emissive) {
          tempVector.normalize();
          const dotProduct = tempVector.dot(new THREE.Vector3(1, 0, 0));
          const normalizedDot = (dotProduct + 1) / 2;
          moon.mesh.material.emissiveIntensity = 0.05 + normalizedDot * 0.15;
        }
      }
    }
  });

  // Animate comet
  if (objects.comet?.group && objects.comet?.body && objects.comet.orbit) {
    const cometTime = elapsedTime * (objects.comet.orbit.speed || 0.06);
    const radius = objects.comet.orbit.radius || 350;
    const height = objects.comet.orbit.height || 20;
    const x = Math.cos(cometTime) * radius;
    const z = Math.sin(cometTime) * (radius * 0.6);
    const y = Math.sin(cometTime) * height;
    objects.comet.group.position.set(x, y, z);
    objects.comet.body.position.copy(objects.comet.group.position);
    if (objects.comet.group.children[1]) {
      objects.comet.group.children[1].rotation.set(0, cometTime, 0);
    }
  }

  // Animate space station
  if (objects.spaceStation?.group && objects.spaceStation?.body && objects.spaceStation.orbit?.target?.group) {
    objects.spaceStation.orbit.angle = (objects.spaceStation.orbit.angle || 0) + (objects.spaceStation.orbit.speed || 0.00005);
    const targetPos = objects.spaceStation.orbit.target.group.position;
    const orbitRadius = objects.spaceStation.orbit.radius || 8;
    const orbitX = Math.cos(objects.spaceStation.orbit.angle) * orbitRadius;
    const orbitZ = Math.sin(objects.spaceStation.orbit.angle) * orbitRadius * 0.8;
    objects.spaceStation.group.position.set(
      targetPos.x + orbitX,
      targetPos.y + Math.sin(objects.spaceStation.orbit.angle * 2) * 2,
      targetPos.z + orbitZ
    );
    objects.spaceStation.group.lookAt(targetPos);
    objects.spaceStation.group.rotation.z += 0.01;
    objects.spaceStation.body.position.copy(objects.spaceStation.group.position);
  }

  // Animate satellites
  objects.satellites?.forEach((satellite, index) => {
    if (satellite?.group && satellite?.body) {
      const orbitSpeed = 0.15 + index * 0.03;
      const orbitRadius = 60 + index * 8;
      updateOrbit(satellite.group, elapsedTime, orbitRadius, orbitSpeed);
      satellite.group.rotation.y += 0.025;
      satellite.body.position.copy(satellite.group.position);
    }
  });

  // Animate UFO
  if (objects.ufo) {
    objects.ufo.position.y = 4 + Math.sin(elapsedTime * 0.8) * 1.5;
    objects.ufo.rotation.y += 0.0005;
  }

  // Animate rocket
  if (objects.rocket) {
    const orbitSpeed = 0.05;
    const orbitRadiusX = 170;
    const orbitRadiusZ = 120;
    const verticalOscillation = 20;
    updateOrbit(objects.rocket, elapsedTime, orbitRadiusX, orbitSpeed);
    objects.rocket.position.z = Math.sin(elapsedTime * orbitSpeed) * orbitRadiusZ;
    objects.rocket.position.y = Math.sin(elapsedTime * orbitSpeed * 2) * verticalOscillation;
    tangent.set(
      -Math.sin(elapsedTime * orbitSpeed) * orbitRadiusX,
      Math.cos(elapsedTime * orbitSpeed * 2) * verticalOscillation * 2 * orbitSpeed,
      Math.cos(elapsedTime * orbitSpeed) * orbitRadiusZ
    ).normalize();
    lookPoint.copy(objects.rocket.position).add(tangent);
    objects.rocket.lookAt(lookPoint);
  }

  // Animate asteroid belt with distance culling
  if (objects.asteroidBelt?.children) {
    const cameraPos = camera.position;
    objects.asteroidBelt.children.forEach(asteroid => {
      const { orbitRadius, orbitSpeed, rotationSpeed } = asteroid.userData || {};
      if (orbitRadius && orbitSpeed && rotationSpeed && cameraPos.distanceTo(asteroid.position) < 1000) {
        updateOrbit(asteroid, elapsedTime, orbitRadius, orbitSpeed);
        asteroid.rotation.x += rotationSpeed.x;
        asteroid.rotation.y += rotationSpeed.y;
        asteroid.rotation.z += rotationSpeed.z;
      }
    });
    objects.asteroidBelt.rotation.y += 0.0005;
  }

  // Update info cards
  if (typeof updateInfoCards === 'function') {
    try {
      updateInfoCards();
    } catch (error) {
      console.error('Error updating info cards:', error);
    }
  }
} 